import { useCallback, useEffect, useState } from 'react';
import difyCapabilityService from '../services/difyCapabilityService';
import { AiDifyCapability, AiDifyCapabilityCreatePayload, AiDifyCapabilityUpdatePayload } from '../types/aiDifyCapability';

export interface UseDifyCapabilityResult {
  readonly capabilities: ReadonlyArray<AiDifyCapability>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly reload: () => void;
  readonly createCapability: (payload: AiDifyCapabilityCreatePayload) => Promise<void>;
  readonly updateCapability: (id: number, payload: AiDifyCapabilityUpdatePayload) => Promise<void>;
  readonly deleteCapability: (id: number) => Promise<void>;
  readonly toggleEnabled: (id: number, enabled: boolean) => Promise<void>;
}

export function useDifyCapability(): UseDifyCapabilityResult {
  const [capabilities, setCapabilities] = useState<ReadonlyArray<AiDifyCapability>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadFlag, setReloadFlag] = useState<number>(0);

  const reload = useCallback((): void => {
    setReloadFlag((prev: number): number => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted: boolean = true;

    const fetchData = async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const list: ReadonlyArray<AiDifyCapability> = await difyCapabilityService.getCapabilities();
        if (isMounted) {
          setCapabilities(list);
        }
      } catch (fetchError) {
        // eslint-disable-next-line no-console
        console.error('useDifyCapability 获取列表失败:', fetchError);
        if (isMounted) {
          setError((fetchError as Error)?.message ?? '获取 Dify 能力配置失败');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData().catch(() => {
      // 已在内部处理
    });

    return (): void => {
      isMounted = false;
    };
  }, [reloadFlag]);

  const createCapability = useCallback(
    async (payload: AiDifyCapabilityCreatePayload): Promise<void> => {
      try {
        await difyCapabilityService.createCapability(payload);
        reload();
      } catch (createError) {
        // eslint-disable-next-line no-console
        console.error('useDifyCapability 创建失败:', createError, payload);
        throw createError;
      }
    },
    [reload]
  );

  const updateCapability = useCallback(
    async (id: number, payload: AiDifyCapabilityUpdatePayload): Promise<void> => {
      try {
        await difyCapabilityService.updateCapability(id, payload);
        reload();
      } catch (updateError) {
        // eslint-disable-next-line no-console
        console.error('useDifyCapability 更新失败:', { id, payload, updateError });
        throw updateError;
      }
    },
    [reload]
  );

  const deleteCapability = useCallback(
    async (id: number): Promise<void> => {
      try {
        await difyCapabilityService.deleteCapability(id);
        reload();
      } catch (deleteError) {
        // eslint-disable-next-line no-console
        console.error('useDifyCapability 删除失败:', id, deleteError);
        throw deleteError;
      }
    },
    [reload]
  );

  const toggleEnabled = useCallback(
    async (id: number, enabled: boolean): Promise<void> => {
      try {
        await difyCapabilityService.toggleEnabled(id, enabled);
        reload();
      } catch (toggleError) {
        // eslint-disable-next-line no-console
        console.error('useDifyCapability 切换启用状态失败:', { id, enabled, toggleError });
        throw toggleError;
      }
    },
    [reload]
  );

  return {
    capabilities,
    loading,
    error,
    reload,
    createCapability,
    updateCapability,
    deleteCapability,
    toggleEnabled
  };
}